/*
 * File: InputPhone.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2020 MacPractice. All rights reserved.
 *
 * Description:  Provides a masked input for a 10-digit phone number. Returns format 123-456-7890
 */

import React from "react";
import PropTypes from "prop-types";
import TextField from "@material-ui/core/TextField";
import { observer } from "mobx-react";
import FilledInput from "@material-ui/core/FilledInput";

const validKeys = new Set(["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"]);
/**
 * Evaluates a key to determine if the key is a dash or a number
 *
 * @param {string} key
 * @return {boolean} true if it is a number, false otherwise
 */
const isNum = key => {
  return validKeys.has(key);
};

const InputPhone = ({ field, value, error, parentOnChange, parentOnBlur }) => {
  /**
   * Applies number key presses to the input field, reformats if necessary, and informs the parent of changes.
   *
   * @param {KeyboardEvent} e
   */
  const applyOrPassCharacter = e => {
    // If the key description is one character, it's a simple character key. Longer descriptions are associated with other keys like arrows and backspace.
    const isStandardKey = e.key.length === 1;
    // If a modifier key is involved this is a key chord of some sort.
    const isChord = e.ctrlKey || e.metaKey || e.altKey;

    let digitCount = 0;
    for (const character of e.target.value) {
      if (isNum(character)) {
        digitCount += 1;
      }
    }

    // Find the index of the selection closest to the end of the field. In the common case this will simply be the insertion cursor at the end of the last typed character.
    let latestLocation;
    if (e.target.selectionStart > e.target.selectionEnd) {
      latestLocation = e.target.selectionStart;
    } else {
      latestLocation = e.target.selectionEnd;
    }

    // If this is a standard key, and a key chord is not involved then the default behavior should be prevented regardless of what the character is.
    if (isStandardKey && !isChord) {
      const rangeLength = Math.abs(
        e.target.selectionStart - e.target.selectionEnd
      );
      // If the character is valid, and the field has room (or will after the selection is replaced) manually perform the update of the field's value.
      if (isNum(e.key) && (digitCount < 10 || rangeLength > 0)) {
        e.target.setRangeText(e.key);
        const newLocation = latestLocation - rangeLength + 1;
        e.target.setSelectionRange(newLocation, newLocation);
      }

      e.preventDefault();
    }

    // Call addFormatting synchronously since the field's value was already updated. This will ensure the logic in addFormatting doesn't need to account for keys being held down, and that the parent is notified of the value the user sees right away.
    addFormatting(e);
  };

  /**
   * Automatically formats US phone number to xxx-xxx-xxxx if the current value is not formatted. If this would not result in a change, the value is not written to the field.
   * The parent callback is always called, with the event passed to this function.
   *
   * Will need to revisit for formatting for international numbers.
   *
   * Note: In some cases this function bails out of formatting the text if it would interfere with the user's ability to keep typing. In these cases the 'onblur' handler will reformat and inform the parent.
   *
   * @param {KeyboardEvent} e
   */
  const addFormatting = e => {
    let val = e.target.value;
    let newVal = val;

    let latestLocation;
    if (e.target.selectionStart > e.target.selectionEnd) {
      latestLocation = e.target.selectionStart;
    } else {
      latestLocation = e.target.selectionEnd;
    }

    // When the insertion point is not at the end of the text field, don't change the contents of the text field. This keeps the cursor from jumping around unpredictably.
    // The value will be corrected when the field loses focus.
    if (latestLocation === val.length) {
      if (val.length === 3 || val.length === 7) {
        // key names that aren't letters, numbers, or symbols are longer than one character. In those cases don't add a dash.
        if (e.key.length === 1 && (isNum(e.key) || e.key === "-")) {
          newVal = val + "-";
        }
      }
    }

    if (newVal !== e.target.value) {
      e.target.value = newVal;
    }

    parentOnChange(e);
  };

  /**
   * Reformat the string completely and check whether the new value matches the old one. If not, the new value is used and the parent is informed of the change.
   *
   * @param {FocusEvent} e
   */
  const handleBlur = e => {
    const reformatted = reformatPhoneString(e.target.value);

    if (reformatted != null) {
      e.target.value = reformatted;
      parentOnChange(e);
    }

    if (typeof parentOnBlur === "function") {
      parentOnBlur();
    }
  };

  /**
   * Reformats the value, compares it against the current value and returns the new value if it is different.
   *
   * @param {string} oldValue The value of the element before formatting.
   * @returns {(string | null)} The new formatted value, or null if no formatting was required.
   */
  const reformatPhoneString = oldValue => {
    let newValue = "";
    let digitCount = 0;
    for (const character of oldValue) {
      if (isNum(character)) {
        newValue += character;
        digitCount += 1;
      }

      if (newValue.length === 3 || newValue.length === 7) {
        newValue += "-";
      }

      if (digitCount === 10) {
        break;
      }
    }

    return newValue;
  };

  return (
    <TextField
      length="12"
      type="tel"
      error={error}
      name={field.name}
      id={field.name}
      defaultValue={value.number}
      onKeyDown={applyOrPassCharacter}
      onBlur={handleBlur}
      label={field.label}
      input={<FilledInput name={field.label} id={field.label} />}
      variant="filled"
      fullWidth
      inputProps={{
        maxLength: 12
      }}
    />
  );
};

InputPhone.propTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.object,
  error: PropTypes.bool,
  parentOnChange: PropTypes.func.isRequired,
  parentOnBlur: PropTypes.func
};

export default observer(InputPhone);
